import type { JSX, ReactNode } from 'react';

import type { ApiFehler } from '../api/fehler';

/**
 * Die Anzeige eines Fehlers (ERR-01, ERR-02, ERR-04).
 *
 * Die drei Teile stehen getrennt und nicht als ein Satz: Was nicht
 * funktioniert hat, welche Auswirkung das hat, was jetzt zu tun ist. In einen
 * Absatz gegossen liest man den dritten Teil nicht mehr — und der ist der
 * einzige, der dem Anwender weiterhilft.
 *
 * `gezeigteFelder` nennt die Felder, die die Maske selbst anzeigt. Alle
 * uebrigen Verstoesse faengt dieser Kasten auf. Ohne das verschwaende ein
 * Verstoss spurlos, sobald der Server ein Feld nennt, das die Maske nicht
 * kennt — und ERR-04 verlangt, dass ALLE angezeigt werden.
 *
 * `role="alert"` sorgt dafuer, dass Vorleseprogramme die Meldung ansagen,
 * sobald sie erscheint. Der Kasten traegt ausserdem tabIndex={-1}, damit die
 * Maske den Fokus hierher setzen kann, wenn kein Feld betroffen ist.
 */
export function Meldung({
  fehler,
  gezeigteFelder = [],
  verweis,
}: {
  readonly fehler: ApiFehler;
  readonly gezeigteFelder?: readonly string[];
  readonly verweis?: React.Ref<HTMLDivElement>;
}): JSX.Element {
  const uebrige = fehler.nichtAmFeld(gezeigteFelder);

  return (
    <div className="meldung meldung--fehler" role="alert" tabIndex={-1} ref={verweis}>
      <p className="meldung__was">{fehler.meldung.wasGeschah}</p>
      <p className="meldung__auswirkung">{fehler.meldung.auswirkung}</p>
      <p className="meldung__schritt">{fehler.meldung.naechsterSchritt}</p>

      {uebrige.length > 0 && (
        <ul className="meldung__verstoesse">
          {uebrige.map((verstoss, stelle) => (
            <li key={`${verstoss.kennung}-${String(stelle)}`}>
              {verstoss.feld !== undefined && (
                <strong className="meldung__feld">{verstoss.feld}: </strong>
              )}
              {verstoss.meldung.wasGeschah} {verstoss.meldung.naechsterSchritt}
            </li>
          ))}
        </ul>
      )}

      <p className="meldung__kennung">
        Kennung <code>{fehler.kennung}</code>, Vorgang <code>{fehler.vorgang}</code>
      </p>
    </div>
  );
}

/**
 * Eine Bestaetigung oder ein Hinweis. Dieselbe Form, damit der Blick sie am
 * selben Ort findet.
 *
 * role="status" statt role="alert": Ein Hinweis unterbricht nicht, was gerade
 * vorgelesen wird. Da er in derselben Aktualisierung entsteht wie sein Inhalt,
 * lesen ihn manche Programme nur zuverlaessig vor, wenn der Fokus darauf
 * gesetzt wird — deshalb tabIndex={-1} und der Verweis.
 */
export function Hinweis({
  ueberschrift,
  children,
  verweis,
}: {
  readonly ueberschrift: string;
  readonly children?: ReactNode;
  readonly verweis?: React.Ref<HTMLDivElement>;
}): JSX.Element {
  return (
    <div className="meldung meldung--hinweis" role="status" tabIndex={-1} ref={verweis}>
      <p className="meldung__was">{ueberschrift}</p>
      {children}
    </div>
  );
}
